import { AlertColor } from "@mui/material";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ampli } from "../../../ampli";
import Modal from "../../../components/common/Modal";
import PrimaryButton from "../../../components/common/PrimaryButton/PrimaryButton";
import BackButton from "../../../components/contract/backButton";
// components
import Gallery from "../../../components/contract/gallery";
import CustomizedSnackbars from "../../../components/customizedSnackbars";
import { ECONTRACT_STATUS } from "../../../enums";
import { cloneContractById } from "../../../setup/api/services/contract";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import NewTransaction from "../../home/newtransaction/newtransaction";
import { CONTRACT_STATUS_AMPLI } from "../constants";
import AdditionalInformation from "../contractDetails/addittionalDetails/additionalDetails";
import { MainDetailsHeading } from "../contractDetails/mainDetails/MainDetailsHeadding";
import { ContractType } from "../contractType";
import { IContractProps } from "../models";
// styles
import styles from "./styles.module.scss";

export interface IRightSection extends IContractProps {
  backHandler: (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | undefined
  ) => void;
}

const RightSection: React.FC<IRightSection> = ({
  contract,
  backHandler,
}: IRightSection): JSX.Element => {
  const [displayDetails, setDisplayDetails] = useState(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [contractId, setContractId] = useState<string | undefined>(undefined);
  const [showModal, setShowModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const userId = AuthConfig.getUserInfo()?.id;

  // alerts
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>();
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  const onClose = () => {
    setShowModal(false);
    window.location.reload();
  };

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const onCancel = useCallback(() => {
    setSnackBarTitle("");
    setSnackBarColor(undefined);
    setSnackBarOpen(false);
  }, []);

  const contractDetails = useCallback(() => {
    return (
      <>
        {contract?.itemDetail?.description && (
          <div className={styles.text_wrapper}>
            <h4>{t("lbl_product_service")}</h4>
            <p className={"p2"}>{contract?.itemDetail?.description}</p>
          </div>
        )}
        <AdditionalInformation contract={contract} />
        {contract?.itemDetail?.images && (
          <Gallery images={contract?.itemDetail?.images} />
        )}
      </>
    );
  }, [contract, t]);

  const setSnackBarStates = useCallback((title: string, color: AlertColor) => {
    setSnackBarOpen(true);
    setSnackBarColor(color);
    setSnackBarTitle(title);
  }, []);

  const toggleMenu = useCallback(() => {
    setShowMenu(!showMenu);
  }, [showMenu]);

  const toggleSuccessModal = useCallback(() => {
    setShowSuccessModal((prevState) => !prevState);
  }, []);

  const sendDuplicateContractEvent = useCallback(
    (contractId: string, contractStatus: string) => {
      try {
        const properties = {
          contractId: contractId,
          contractDuplicationDate: String(new Date()),
          contractStatus: CONTRACT_STATUS_AMPLI[contractStatus],
        };
        ampli && ampli.duplicateContract(properties);
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    []
  );

  const cloneContract = useCallback(async () => {
    type Error = {
      error: {
        message: string;
        status: string;
      };
    };
    try {
      const res = (await cloneContractById(contract?.id)) as unknown as {
        data: { id: string };
      };
      sendDuplicateContractEvent(contract?.shortId, contract?.status);
      setContractId(res?.data?.id);
      setSnackBarStates(t("lbl_action_under_processing"), "success");
      toggleSuccessModal();
      setShowMenu(false);
      onCancel();
    } catch (error) {
      const errRes: Error = error as unknown as Error;
      setSnackBarStates(`${JSON.stringify(errRes?.error?.message)}`, "error");
      setShowMenu(false);
      onCancel();
    }
  }, [
    contract?.id,
    contract?.shortId,
    contract?.status,
    onCancel,
    sendDuplicateContractEvent,
    setSnackBarStates,
    t,
    toggleSuccessModal,
  ]);

  const renderDropDown = useCallback(() => {
    return (
      <>
        <div className={styles.top_section_drop_down}>
          <div
            onClick={cloneContract}
            className={classNames(styles.row, styles["mv1"])}>
            <img alt="menu" src="/assets/images/svg/copy.svg" />
            <p className={classNames(styles["p2"], styles["black"])}>
              {t("lbl_duplocate_contract_action")}
            </p>
          </div>
          {contract?.status === ECONTRACT_STATUS["CREATED"] && (
            <>
              <div className={styles.divider} />
              <div
                onClick={openModal}
                className={classNames(styles["row"], styles["mv1"])}>
                <img
                  className={styles.edit_menu}
                  alt="menu"
                  src="/assets/images/svg/edit.svg"
                />
                <p className={classNames(styles["p2"], styles["black"])}>
                  {t("lbl_edit_contract")}
                </p>
              </div>
            </>
          )}
        </div>
      </>
    );
  }, [cloneContract, contract?.status, openModal, t]);

  const renderSnackBar = useCallback(() => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  }, [snackBarColor, snackBarOpen, snackBarTitle]);

  const navigateToContract = useCallback(async () => {
    navigate(`/contract/${contractId}`);
    setShowMenu(false);
    setShowSuccessModal(false);
  }, [contractId, navigate]);

  const renderSuccess = useCallback(() => {
    return (
      <Modal
        visible={showSuccessModal}
        onCancel={toggleSuccessModal}
        size="small">
        <div className={classNames(styles.center, styles.modalContainer)}>
          <img alt="success" src="/assets/images/svg/Success.svg" />
          <h2>{t("lbl_transaction_copied_successfully")}</h2>
          <PrimaryButton onClick={navigateToContract}>
            <h3 className={styles["white"]}>{t("lbl_view_the_transaction")}</h3>
          </PrimaryButton>
        </div>
      </Modal>
    );
  }, [navigateToContract, showSuccessModal, t, toggleSuccessModal]);

  return (
    <>
      <div className={classNames(styles.right_container)}>
        <div className={styles.top_section}>
          <div className={styles.top_section_right}>
            <BackButton onClick={backHandler} />
            <div className={"mr2"}>
              <ContractType contract={contract} />
            </div>
          </div>
          {Number(userId) === contract?.provider?.id && (
            <div className={styles.top_section_left} onClick={toggleMenu}>
              <img alt="menu" src="/assets/images/svg/menuAlt.svg" />
            </div>
          )}
          {showMenu &&
            Number(userId) === contract?.provider?.id &&
            renderDropDown()}
        </div>
        <div className={styles.heading_wrapper}>
          <MainDetailsHeading contract={contract} />
        </div>
        <div className={styles.contract_details_desktop}>
          {contractDetails()}
        </div>
        <div className={styles.contract_details_mobile}>
          {displayDetails ? (
            <>{contractDetails()}</>
          ) : (
            <div
              onClick={() => setDisplayDetails(!displayDetails)}
              className={styles.hideDetails}>
              <p className={classNames("subtitle2", "primary")}>
                {t("lbl_hide_link")}
              </p>
            </div>
          )}
        </div>
      </div>
      {renderSuccess()}
      {renderSnackBar()}
      <NewTransaction
        showModal={showModal}
        onClose={onClose}
        editContract={contract}
      />
    </>
  );
};

export default RightSection;
